import { WallboxConfigurationModel } from 'src/Libs/wallboxes'
import { chargeStrategyConfigurationToForm, weekdayToForm } from 'src/Libs/wallboxes/transformers/weekday-form.transformer'
import {
  CombinedChargeStrategyConfigurationForm,
  WallboxSettingsFormModel,
} from '../../models/wallbox-configuration/wallbox-configuration-settings-form.model'
import { CHARGING_DAYS } from '@e3dc-react/shell/Libs/date/types'
import { ChargeStrategyEnum, WallboxType } from '../../dto/types'
import { ChargeStrategyTypeEnum, CombinedChargeStrategyDto } from '../../types'
import { WallboxOcppModel } from '../../models'
import { FALLBACK_VALUE_EDSN } from '../../components'

const initialChargeStrategy: CombinedChargeStrategyDto = {
  chargeStrategyType: ChargeStrategyTypeEnum.combined,
}

export const initialChargeStrategyConfiguration: CombinedChargeStrategyConfigurationForm[] = CHARGING_DAYS.map((_, index) => ({
  chargeStrategyWeekday: index,
  chargeStrategyUnit: 0,
  chargeStrategyDirectChargeAmount: 0,
  chargeStrategyDelayedChargeAmount: 0,
  chargeStrategyDelayedChargeTime: '00:00',
  chargeStrategyTotalChargeAmount: 0,
}))

const LED_INTENSITY_FALLBACK = 100
const CABLE_LOCK_FALLBACK = false

/**
 * Transform WallbaxConfiguraton model to WallboxSettingsFormModel to be used
 * by wallbox settings form
 */
const settingsModelToSettingsFormTransformer = (
  model?: WallboxConfigurationModel,
  ocppSettings?: WallboxOcppModel | null
): WallboxSettingsFormModel => {
  const wallboxType = model?.wallboxType || WallboxType.DefaultE3dc

  const formValues = {
    /* activeChargeStrategy used only in software version < X_2024_04 */
    activeChargeStrategy: model?.activeChargeStrategy ?? ChargeStrategyEnum.deactivated,

    authRequired: Boolean(model?.authRequired),
    autoPhaseSwitchEnabled: Boolean(model?.autoPhaseSwitchEnabled),
    bidiModeActive: Boolean(model?.bidiModeActive),
    chargeStopHysteresis: model?.chargeStopHysteresis ?? 0,
    ledIntensity: model?.ledIntensity ?? LED_INTENSITY_FALLBACK,
    cableLock: model?.cableLock ?? CABLE_LOCK_FALLBACK,
    wallboxType,

    /* chargeStrategy, chargeStrategyConfiguration used only in software version >= X_2024_04 */
    chargeStrategy: model?.chargeStrategy || initialChargeStrategy,
    chargeStrategyConfiguration:
      model?.chargeStrategyConfiguration?.map(configuration => chargeStrategyConfigurationToForm(configuration, wallboxType)) ??
      initialChargeStrategyConfiguration,

    /* 0: wallbox limited by current (A); 1: wallbox limited by power (W); undefined: treat as 0; writable: no */
    chargeLimitType: model?.chargeLimitType ?? 0,

    deviceName: model?.deviceName ?? '',
    dhcpEnabled: model?.dhcpEnabled ?? false,
    diagBatCapacity: model?.diagBatCapacity ?? 0,
    gateway: model?.gateway ?? '',
    ipAddress: model?.ipAddr ?? '',
    keyLockMode: model?.keyLockMode,
    lowerCurrentLimit: model?.lowerCurrentLimit ?? 0,
    maxChargeCurrent: model?.maxChargeCurrent ?? 0,
    minChargeCurrent: model?.minChargeCurrent ?? 0,

    /* NOTE: we have to adjust fallback values for other wallboxes in future */
    minChargePower: model?.minChargePower ?? FALLBACK_VALUE_EDSN.minChargePower,
    maxChargePower: model?.maxChargePower ?? FALLBACK_VALUE_EDSN.maxChargePower,
    maxDischargePower: model?.maxDischargePower ?? FALLBACK_VALUE_EDSN.maxDischargePower,
    lowerChargePowerLimit: model?.lowerChargePowerLimit ?? FALLBACK_VALUE_EDSN.lowerChargePowerLimit,
    upperChargePowerLimit: model?.upperChargePowerLimit ?? FALLBACK_VALUE_EDSN.upperChargePowerLimit,
    lowerDischargePowerLimit: model?.lowerDischargePowerLimit ?? FALLBACK_VALUE_EDSN.lowerDischargePowerLimit,
    upperDischargePowerLimit: model?.upperDischargePowerLimit ?? FALLBACK_VALUE_EDSN.upperDischargePowerLimit,

    numberPhases: model?.numberPhases ?? 1,
    rfidReaderEnabled: model?.rfidReaderEnabled,
    subnetMask: model?.subnetMask ?? '',
    sunModeActive: model?.sunModeActive ?? false,
    upperCurrentLimit: model?.upperCurrentLimit ?? 0,

    /* weekdays used only in software version < X_2024_04 */
    weekdays: model?.weekdays?.map(weekdayToForm) ?? [],

    ocppEnabled: ocppSettings?.enabled ?? false,
    ocppId: ocppSettings?.id ?? '',
    ocppServerAddress: ocppSettings?.serverAddress,
    ocppAuthType: ocppSettings?.authType,
    ocppAllowLocalCharge: ocppSettings?.allowLocalCharge,
    ocppPassword: ocppSettings?.password ?? '',
  }

  return formValues
}
export default settingsModelToSettingsFormTransformer
